import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Sync a single string state value to a URL search param.
 *
 * - Reads the current value from the URL on each render.
 * - Writes via setSearchParams with `replace: true` so the back button keeps
 *   working (one entry per actual navigation, not per state change).
 * - Setting the value to `null` (when the type allows) deletes the param.
 *
 * Returned tuple is `[value, setValue]` to mirror useState ergonomics.
 */
export function useUrlState<T extends string | null>(
  key: string,
  defaultValue: T,
): readonly [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);
  const value = (raw ?? defaultValue) as T;

  const setValue = useCallback(
    (next: T) => {
      setSearchParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (next == null || next === '') {
            out.delete(key);
          } else {
            out.set(key, String(next));
          }
          return out;
        },
        { replace: true },
      );
    },
    [key, setSearchParams],
  );

  return [value, setValue] as const;
}
